import React, { useRef, useState } from 'react';
import { s_login, s_login_filled, s_form, s_nophone, s_dashboard } from './manualScreenshots';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/* ─── helpers ─────────────────────────────────────────────── */
const Section = ({ num, title, children }) => (
  <section style={{ marginBottom: 48, pageBreakInside: 'avoid' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, borderBottom: '2px solid #1a5c2e', paddingBottom: 10 }}>
      <div style={{ width: 32, height: 32, background: '#1a5c2e', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{num}</span>
      </div>
      <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 20, color: '#1a5c2e', margin: 0 }}>{title}</h2>
    </div>
    {children}
  </section>
);

const SubSection = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <h3 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 15, color: '#2d8a4e', marginBottom: 10, paddingLeft: 10, borderLeft: '3px solid #3aaa62' }}>{title}</h3>
    {children}
  </div>
);

const Screenshot = ({ src, caption, scale = 1 }) => (
  <div style={{ margin: '14px 0', textAlign: 'center' }}>
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', display: 'inline-block', maxWidth: '100%' }}>
      <img src={src} alt={caption} style={{ width: '100%', maxWidth: 900 * scale, display: 'block' }} />
    </div>
    {caption && <p style={{ fontSize: 12, color: '#888', marginTop: 6, fontStyle: 'italic' }}>{caption}</p>}
  </div>
);

const Field = ({ label, value, required }) => (
  <div style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
    <span style={{ fontSize: 13, color: '#666', width: 200, flexShrink: 0 }}>
      {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
    </span>
    <span style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 500 }}>{value}</span>
  </div>
);

const Check = ({ ok = true, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
    <div style={{ width: 22, height: 22, borderRadius: 6, background: ok ? '#f0faf3' : '#fff3cd', border: `1px solid ${ok ? '#cfe3d6' : '#fde68a'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
      <span style={{ fontSize: 12 }}>{ok ? '✅' : '⬜'}</span>
    </div>
    <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{children}</span>
  </div>
);

const InfoBox = ({ icon, color, bg, border, title, children }) => (
  <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
    <p style={{ color, fontWeight: 700, fontSize: 13, margin: '0 0 5px' }}>{icon} {title}</p>
    <p style={{ color: '#444', fontSize: 13, margin: 0, lineHeight: 1.65 }}>{children}</p>
  </div>
);

/* ─── Email preview components ─────────────────────────────── */
const EmailWelcomePreview = () => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', fontFamily: 'Arial, sans-serif', maxWidth: 620, margin: '0 auto', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
    {/* Top bar */}
    <div style={{ height: 5, background: '#0d3d1f' }} />
    {/* Header */}
    <div style={{ background: 'linear-gradient(135deg, #1a5c2e, #3aaa62)', padding: '32px 36px', textAlign: 'center' }}>
      <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '8px 20px', marginBottom: 18 }}>
        <span style={{ color: '#fff', fontWeight: 800, fontSize: 17, letterSpacing: 3 }}>VALORIZA</span>
      </div>
      <div style={{ fontSize: 36, marginBottom: 10 }}>🎉</div>
      <h2 style={{ color: '#fff', margin: 0, fontSize: 22, fontWeight: 700 }}>¡Bienvenido/a al equipo!</h2>
      <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0', fontSize: 14 }}>Nos alegra tenerte con nosotros en Valoriza Servicios Medioambientales</p>
    </div>
    {/* Body */}
    <div style={{ padding: '24px 32px', background: '#fff' }}>
      <p style={{ color: '#333', fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>
        Estimado/a <strong style={{ color: '#1a5c2e' }}>Juan Martínez López</strong>,<br />
        Tu alta en los sistemas IT de Valoriza ha sido procesada correctamente. A continuación encontrarás toda la información que necesitas para comenzar.
      </p>
      {/* Data card */}
      <div style={{ background: '#f0f8f2', border: '1px solid #cfe3d6', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
        <p style={{ color: '#0d3d1f', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>📋 Tus datos de acceso</p>
        {[['Correo corporativo','j.martinez@valorizasm.com'],['Departamento','Operaciones'],['Puesto','Técnico Medioambiental'],['Incorporación','01/07/2026']].map(([l,v]) => (
          <div key={l} style={{ display: 'flex', padding: '5px 0', borderTop: '1px solid #d9ead3', fontSize: 13 }}>
            <span style={{ color: '#666', width: 160 }}>{l}</span>
            <span style={{ fontWeight: 600, color: l === 'Correo corporativo' ? '#1a5c2e' : '#1a1a1a' }}>{v}</span>
          </div>
        ))}
      </div>
      {/* Password notice */}
      <div style={{ background: '#fff8e1', border: '1px solid #fde68a', borderLeft: '4px solid #f9a825', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
        <p style={{ color: '#e65100', fontSize: 12, fontWeight: 700, margin: '0 0 4px' }}>🔐 Información importante</p>
        <p style={{ color: '#5d4037', fontSize: 13, margin: 0 }}>Recibirás tu <strong>contraseña temporal en un segundo correo separado</strong>, por razones de seguridad.</p>
      </div>
      {/* Survey123 */}
      <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ background: '#1a5c2e', padding: '10px 16px' }}><p style={{ color: '#fff', fontSize: 13, fontWeight: 700, margin: 0 }}>📦 Entrega de material IT</p></div>
        <div style={{ padding: '14px 16px' }}>
          <p style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>Rellena el <strong>formulario de recibí del material IT</strong> escaneando el código QR de los dispositivos o accediendo aquí:</p>
          <a href="https://arcg.is/15WDvq0" style={{ display: 'inline-block', background: '#2d8a4e', color: '#fff', padding: '9px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>📝 Completar Formulario Recibí →</a>
        </div>
      </div>
      {/* Buenas prácticas */}
      <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ background: '#1a5c2e', padding: '10px 16px' }}><p style={{ color: '#fff', fontSize: 13, fontWeight: 700, margin: 0 }}>🔒 Buenas prácticas en tecnología</p></div>
        <div style={{ padding: '14px 16px' }}>
          <p style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>Por favor, accede a la <strong>guía de buenas prácticas</strong> y fírmala lo antes posible.</p>
          <a href="https://myhub.valorizasm.com" style={{ display: 'inline-block', background: '#2d8a4e', color: '#fff', padding: '9px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>📖 Acceder a la Guía →</a>
        </div>
      </div>
      {/* Accesos internos */}
      <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ background: '#1a5c2e', padding: '10px 16px' }}><p style={{ color: '#fff', fontSize: 13, fontWeight: 700, margin: 0 }}>🔧 Accesos y recursos internos</p></div>
        <div style={{ padding: '14px 16px' }}>
          <div style={{ paddingBottom: 10, borderBottom: '1px solid #e0e0e0', marginBottom: 10 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#1a5c2e', margin: '0 0 3px' }}>Portal TREBOL (peticiones e incidencias)</p>
            <a href="https://trebol.valorizasm.com/login" style={{ color: '#2d8a4e', fontSize: 12, textDecoration: 'none', fontWeight: 600 }}>🔗 Acceder a TREBOL →</a>
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#1a5c2e', margin: '0 0 3px' }}>Intranet corporativa (MyHub)</p>
            <a href="https://myhub.valorizasm.com" style={{ color: '#2d8a4e', fontSize: 12, textDecoration: 'none', fontWeight: 600 }}>🔗 Acceder a MyHub →</a>
          </div>
        </div>
      </div>
    </div>
    {/* Footer */}
    <div style={{ background: 'linear-gradient(135deg, #1a5c2e, #2d8a4e)', padding: '24px 32px', textAlign: 'center' }}>
      <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, margin: '0 0 5px' }}>¿Dudas? Contacta con el equipo IT:</p>
      <p style={{ color: '#7fe5a0', fontSize: 13, fontWeight: 600, margin: '0 0 14px' }}>innovation@valorizasm.com</p>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, margin: '0 0 3px' }}>© 2026 Valoriza Servicios Medioambientales</p>
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, margin: 0, fontStyle: 'italic' }}>Powered by Valoriza's Innovation Team</p>
    </div>
    <div style={{ height: 5, background: '#0d3d1f' }} />
  </div>
);

const EmailPasswordPreview = () => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', fontFamily: 'Arial, sans-serif', maxWidth: 620, margin: '0 auto', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
    <div style={{ height: 5, background: '#0d3d1f' }} />
    <div style={{ background: 'linear-gradient(135deg, #1a5c2e, #3aaa62)', padding: '32px 36px', textAlign: 'center' }}>
      <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '8px 20px', marginBottom: 16 }}>
        <span style={{ color: '#fff', fontWeight: 800, fontSize: 17, letterSpacing: 3 }}>VALORIZA</span>
      </div>
      <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, padding: '5px 16px', marginLeft: 12 }}>
        <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>🔐 CREDENCIALES DE ACCESO</span>
      </div>
      <h2 style={{ color: '#fff', margin: '14px 0 6px', fontSize: 22, fontWeight: 700 }}>Tu contraseña temporal</h2>
      <p style={{ color: 'rgba(255,255,255,0.75)', margin: 0, fontSize: 13 }}>Acceso: <strong style={{ color: '#fff' }}>j.martinez@valorizasm.com</strong></p>
    </div>
    <div style={{ padding: '24px 32px', background: '#fff' }}>
      <p style={{ color: '#333', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
        Hola <strong style={{ color: '#1a5c2e' }}>Juan Martínez López</strong>,<br />
        Aquí tienes tu contraseña de acceso temporal. <strong>Cámbiala en cuanto accedas</strong> por primera vez.
      </p>
      {/* Password box */}
      <div style={{ background: '#1a5c2e', borderRadius: 12, padding: '28px', textAlign: 'center', marginBottom: 20 }}>
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, margin: '0 0 12px' }}>Contraseña temporal</p>
        <div style={{ background: 'rgba(255,255,255,0.12)', border: '2px dashed rgba(255,255,255,0.35)', borderRadius: 8, padding: '14px 28px', display: 'inline-block' }}>
          <span style={{ color: '#fff', fontSize: 26, fontWeight: 700, letterSpacing: 4, fontFamily: 'Courier New, monospace' }}>JuanM#2026!</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, margin: '12px 0 0' }}>Solo para el primer acceso · Cámbiala inmediatamente</p>
      </div>
      {/* Steps */}
      <div style={{ background: '#f0f8f2', border: '1px solid #cfe3d6', borderRadius: 10, padding: '16px 20px', marginBottom: 14 }}>
        <p style={{ color: '#0d3d1f', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, margin: '0 0 12px' }}>📋 Pasos para el primer acceso</p>
        {[
          ['1.', 'Accede a Office 365: portal.office.com'],
          ['2.', 'Introduce tu correo: j.martinez@valorizasm.com'],
          ['3.', 'Usa la contraseña temporal indicada arriba'],
          ['4.', 'El sistema te pedirá cambiar la contraseña inmediatamente'],
        ].map(([n, t]) => (
          <div key={n} style={{ padding: '5px 0', borderTop: '1px solid #d9ead3', fontSize: 13, color: '#444' }}>
            <span style={{ color: '#2d8a4e', fontWeight: 700 }}>{n}</span> {t}
          </div>
        ))}
      </div>
      {/* Security */}
      <div style={{ background: '#fff8e1', border: '1px solid #fde68a', borderLeft: '4px solid #f9a825', borderRadius: 8, padding: '12px 16px' }}>
        <p style={{ color: '#e65100', fontSize: 12, fontWeight: 700, margin: '0 0 4px' }}>⚠️ Aviso de seguridad</p>
        <p style={{ color: '#5d4037', fontSize: 12, margin: 0, lineHeight: 1.6 }}>Nunca compartas esta contraseña. El equipo IT de Valoriza <strong>jamás te pedirá tu contraseña</strong> por email o teléfono.</p>
      </div>
    </div>
    <div style={{ background: 'linear-gradient(135deg, #1a5c2e, #2d8a4e)', padding: '24px 32px', textAlign: 'center' }}>
      <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, margin: '0 0 5px' }}>¿Problemas de acceso? Contacta con el equipo IT:</p>
      <p style={{ color: '#7fe5a0', fontSize: 13, fontWeight: 600, margin: '0 0 14px' }}>innovation@valorizasm.com · Portal TREBOL</p>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, margin: '0 0 3px' }}>© 2026 Valoriza Servicios Medioambientales · Información confidencial</p>
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, margin: 0, fontStyle: 'italic' }}>Powered by Valoriza's Innovation Team</p>
    </div>
    <div style={{ height: 5, background: '#0d3d1f' }} />
  </div>
);

/* ─── Main component ─────────────────────────────────────────── */
export default function ManualPage({ onBack }) {
  const printRef = useRef();
  const [isGenerating, setIsGenerating] = useState(false);

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    setIsGenerating(true);
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        windowWidth: 1200
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;
      
      pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save('Manual_Validacion_MVP_Valoriza.pdf');
    } catch (error) {
      // Error is shown to user via alert, no need for console in production
      alert('Hubo un error al generar el PDF. Por favor, intenta de nuevo.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="page-container" style={{ background: '#f4f7f4', minHeight: '100vh', paddingBottom: 60, padding: 0 }}>
      {/* Toolbar (hidden on print) */}
      <div className="no-print" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#fff', borderBottom: '1px solid #e0e0e0',
        padding: '12px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={onBack} style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Volver
          </button>
          <div style={{ background: 'linear-gradient(135deg, #1a5c2e, #3aaa62)', borderRadius: 8, padding: '6px 14px' }}>
            <img src="/valoriza-logo.svg" alt="Valoriza" style={{ height: 22, display: 'block' }} />
          </div>
          <div>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a5c2e' }}>Manual de Validación MVP</div>
            <div style={{ fontSize: 11, color: '#999' }}>Sistema de Alta IT · Versión 1.0 · Abril 2026</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handlePrint} style={{
            background: '#2d8a4e', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'IBM Plex Sans, sans-serif',
          }}>
            🖨️ Imprimir
          </button>
          <button onClick={handleDownloadPDF} disabled={isGenerating} style={{
            background: isGenerating ? '#999' : '#1a5c2e', 
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 22px', cursor: isGenerating ? 'not-allowed' : 'pointer', 
            fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'IBM Plex Sans, sans-serif',
          }}>
            {isGenerating ? '⏳ Generando...' : '📥 Descargar PDF'}
          </button>
        </div>
      </div>

      {/* Document */}
      <div ref={printRef} style={{ maxWidth: 960, margin: '32px auto', padding: '0 24px' }}>

        {/* ═══ COVER PAGE ═══ */}
        <div style={{ background: 'linear-gradient(150deg, #0d3d1f 0%, #1a5c2e 45%, #2d8a4e 80%, #3aaa62 100%)', borderRadius: 20, padding: '60px 56px', marginBottom: 40, position: 'relative', overflow: 'hidden', pageBreakAfter: 'always' }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 280, height: 280, borderRadius: '50%', background: 'rgba(110,201,98,0.08)', border: '1px solid rgba(110,201,98,0.15)' }} />
          <div style={{ position: 'absolute', bottom: -60, right: 60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(110,201,98,0.06)', border: '1px solid rgba(110,201,98,0.1)' }} />
          <div style={{ position: 'relative' }}>
            <img src="/valoriza-logo.svg" alt="Valoriza" style={{ height: 52, marginBottom: 40 }} />
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 24, padding: '5px 18px', marginBottom: 24 }}>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: 600, letterSpacing: 1 }}>DOCUMENTO DE VALIDACIÓN · MVP v1.0</span>
            </div>
            <h1 style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#fff', fontSize: 38, fontWeight: 800, margin: '0 0 12px', lineHeight: 1.2 }}>
              Altas de IT<br /><span style={{ color: '#7fe5a0' }}>Plataforma Corporativa</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, margin: '0 0 48px', lineHeight: 1.7, maxWidth: 540 }}>
              Sistema de gestión de altas de empleados IT con envío automático de emails de bienvenida y credenciales. Panel de control con KPIs y seguimiento en tiempo real.
            </p>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'Fecha', value: 'Abril 2026' },
                { label: 'Versión', value: 'MVP 1.0' },
                { label: 'Preparado por', value: "Valoriza's Innovation Team" },
                { label: 'Estado', value: 'En validación ✓' },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '12px 20px' }}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>{label}</div>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ DOCUMENT BODY ═══ */}
        <div style={{ background: '#fff', borderRadius: 16, padding: '48px 56px', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>

          {/* TABLE OF CONTENTS */}
          <div style={{ marginBottom: 48, background: '#f9fdf9', border: '1px solid #cfe3d6', borderRadius: 12, padding: '24px 28px' }}>
            <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 17, color: '#1a5c2e', margin: '0 0 16px', paddingBottom: 10, borderBottom: '1px solid #cfe3d6' }}>Índice de contenidos</h2>
            {[
              ['1', 'Descripción del sistema y arquitectura'],
              ['2', 'Acceso al sistema — Pantalla de login'],
              ['3', 'Flujo del formulario de alta'],
              ['4', 'Emails enviados al empleado'],
              ['5', 'Pantalla de confirmación'],
              ['6', 'Panel de control y KPIs'],
              ['7', 'Funcionalidades adicionales'],
              ['8', 'Políticas de seguridad de Valoriza'],
              ['9', 'Checklist de validación MVP'],
            ].map(([n, t]) => (
              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid #f0f8f2', fontSize: 14, color: '#374151' }}>
                <span style={{ width: 24, height: 24, background: '#1a5c2e', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{n}</span>
                {t}
              </div>
            ))}
          </div>

          {/* SECTION 1 */}
          <Section num="1" title="Descripción del sistema y arquitectura">
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 20 }}>
              El sistema <strong>Altas de IT · Plataforma Corporativa</strong> es una herramienta interna desarrollada para el equipo CAU de Valoriza que permite registrar nuevas altas de empleados y automatizar el proceso de bienvenida mediante el envío de dos correos electrónicos corporativos.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 24 }}>
              {[
                { icon: '📋', title: 'Formulario de Alta', desc: 'Relleno por técnicos CAU con datos del nuevo empleado: nombre, contacto, puesto, departamento y credenciales IT.' },
                { icon: '📧', title: 'Emails Automáticos', desc: '2 emails branded enviados vía Mandrill: (1) Bienvenida con recursos y links, (2) Contraseña temporal en correo separado.' },
                { icon: '📊', title: 'Panel de Control', desc: 'Dashboard con KPIs, gráficos de tendencia, estado de emails, tabla de registros, filtros y exportación CSV.' },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{ background: '#f9fdf9', border: '1px solid #cfe3d6', borderRadius: 10, padding: '16px' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
                  <p style={{ fontWeight: 700, fontSize: 14, color: '#1a5c2e', margin: '0 0 6px' }}>{title}</p>
                  <p style={{ fontSize: 12, color: '#666', margin: 0, lineHeight: 1.6 }}>{desc}</p>
                </div>
              ))}
            </div>
            <SubSection title="Stack tecnológico">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  ['Frontend', 'React 18 + Framer Motion + Recharts + Shadcn/UI'],
                  ['Backend', 'FastAPI (Python) + Motor (MongoDB async)'],
                  ['Base de datos', 'MongoDB · Almacenamiento de registros de altas'],
                  ['Email', 'Mandrill (Mailchimp Transactional) · API key configurada'],
                  ['Autenticación', 'Session-based (sessionStorage) · Usuario único CAU'],
                  ['URL de acceso', 'https://it-onboarding-mvp.preview.emergentagent.com'],
                ].map(([k, v]) => (
                  <div key={k} style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 8, fontSize: 13 }}>
                    <span style={{ fontWeight: 700, color: '#1a5c2e' }}>{k}:</span> <span style={{ color: '#444' }}>{v}</span>
                  </div>
                ))}
              </div>
            </SubSection>
            <SubSection title="Flujo general del proceso">
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', marginTop: 8 }}>
                {['Login CAU', 'Rellenar formulario', 'Enviar alta', 'Email bienvenida\n(→ empleado + CC IT/RRHH)', 'Email contraseña\n(→ empleado, privado)', 'Registro en dashboard'].map((step, i, arr) => (
                  <React.Fragment key={step}>
                    <div style={{ background: i % 2 === 0 ? '#1a5c2e' : '#2d8a4e', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, textAlign: 'center', minWidth: 100 }}>
                      {step.split('\n').map((l, li) => <div key={li}>{l}</div>)}
                    </div>
                    {i < arr.length - 1 && <div style={{ color: '#ccc', fontSize: 20, padding: '0 4px' }}>→</div>}
                  </React.Fragment>
                ))}
              </div>
            </SubSection>
          </Section>

          {/* SECTION 2 */}
          <Section num="2" title="Acceso al sistema — Pantalla de login">
            <InfoBox icon="🔐" color="#1a5c2e" bg="#f0faf3" border="#cfe3d6" title="Credenciales de acceso">
              <><strong>Usuario:</strong> altausuarios@valorizasm.com &nbsp;·&nbsp; <strong>Contraseña:</strong> Val0rizaAlta26!<br />
              El acceso está restringido al personal CAU autorizado. La sesión persiste mientras el navegador esté abierto.</>
            </InfoBox>
            <Screenshot src={s_login} caption="Pantalla de login — Diseño split-screen con branding Valoriza, partículas animadas y formulario de acceso." />
            <SubSection title="Características de la pantalla de login">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                {[
                  '✅ Logo oficial Valoriza con animación flotante y anillos concéntricos pulsantes',
                  '✅ Partículas animadas y grid mesh sobre fondo degradado verde corporativo',
                  '✅ 3 feature pills explicando las capacidades del sistema',
                  '✅ Formulario con validación, ojo para ver/ocultar contraseña',
                  '✅ Animación "shake" si las credenciales son incorrectas',
                  '✅ Mensaje de éxito animado antes de entrar al sistema',
                ].map((t, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#555', padding: '8px 12px', background: '#fafafa', borderRadius: 8, lineHeight: 1.6 }}>{t}</div>
                ))}
              </div>
            </SubSection>
            <Screenshot src={s_login_filled} caption="Credenciales introducidas antes de pulsar 'Acceder al sistema'." />
          </Section>

          {/* SECTION 3 */}
          <Section num="3" title="Flujo del formulario de alta">
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 16 }}>
              El técnico CAU rellena el formulario con los datos del nuevo empleado. El formulario está organizado en <strong>tres secciones</strong> en un layout de dos columnas que aprovecha todo el espacio de pantalla.
            </p>
            <Screenshot src={s_form} caption="Formulario de Alta de Empleado IT — Layout 2 columnas con todas las secciones visibles." />

            <SubSection title="Campos del formulario">
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ background: '#1a5c2e', padding: '10px 16px' }}>
                  <p style={{ color: '#fff', fontSize: 12, fontWeight: 700, margin: 0 }}>📋 Datos personales</p>
                </div>
                <div style={{ padding: '4px 16px 8px' }}>
                  <Field label="Nombre y apellidos" value="Texto libre" required />
                  <Field label="Correo personal" value="Email válido — aquí llegan los emails de bienvenida" required />
                  <Field label="Teléfono" value="Formato internacional (+34 XXX XXX XXX)" required />
                  <Field label="Fecha de incorporación" value="Selector de fecha" required />
                </div>
                <div style={{ background: '#2d8a4e', padding: '10px 16px' }}>
                  <p style={{ color: '#fff', fontSize: 12, fontWeight: 700, margin: 0 }}>🏢 Información laboral</p>
                </div>
                <div style={{ padding: '4px 16px 8px' }}>
                  <Field label="Departamento" value="Dropdown: 14 opciones (Operaciones, Tecnología, RRHH…)" required />
                  <Field label="Puesto de trabajo" value="Texto libre" required />
                </div>
                <div style={{ background: '#3aaa62', padding: '10px 16px' }}>
                  <p style={{ color: '#fff', fontSize: 12, fontWeight: 700, margin: 0 }}>🔐 Credenciales de acceso IT</p>
                </div>
                <div style={{ padding: '4px 16px 8px' }}>
                  <Field label="Correo corporativo" value="Email @valorizasm.com" required />
                  <Field label="Contraseña temporal" value="Mínimo 4 caracteres — se enviará en email separado" required />
                </div>
              </div>
            </SubSection>

            <SubSection title="Funcionalidad especial: Sin teléfono">
              <InfoBox icon="📵" color="#2d8a4e" bg="#f0faf3" border="#cfe3d6" title='Toggle "Sin teléfono"'>
                Cuando el empleado no dispone de teléfono corporativo en el momento del alta, el técnico CAU puede pulsar el botón <strong>"Sin teléfono"</strong> junto al campo. El campo se desactiva automáticamente, deja de ser obligatorio y se registra como "No disponible".
              </InfoBox>
              <Screenshot src={s_nophone} caption='Campo de teléfono desactivado tras activar el toggle "Sin teléfono".' scale={0.85} />
            </SubSection>

            <SubSection title="Validación del formulario">
              <p style={{ fontSize: 13, color: '#555', lineHeight: 1.7, marginBottom: 10 }}>Antes de enviar, el sistema valida que:</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  'Todos los campos obligatorios están rellenos',
                  'Los correos tienen formato válido (usuario@dominio.ext)',
                  'El teléfono tiene formato válido (si no está desactivado)',
                  'La contraseña tiene mínimo 4 caracteres',
                  'Se ha seleccionado un departamento del desplegable',
                  'Se ha indicado una fecha de incorporación',
                ].map((v, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 12px', background: '#fafafa', borderRadius: 8, fontSize: 12, color: '#444', alignItems: 'flex-start' }}>
                    <span style={{ color: '#3aaa62', flexShrink: 0 }}>✓</span> {v}
                  </div>
                ))}
              </div>
            </SubSection>
          </Section>

          {/* SECTION 4 */}
          <Section num="4" title="Emails enviados al empleado">
            <InfoBox icon="📧" color="#1565c0" bg="#e8f4fd" border="#90caf9" title="Sistema de envío — Mandrill (Mailchimp Transactional)">
              Los emails se envían en tiempo real desde <strong>innovation@valorizasm.com</strong> usando la plataforma Mandrill. El sistema envía <strong>dos correos separados</strong> por seguridad: el primero con la bienvenida e información, el segundo solo con la contraseña.
            </InfoBox>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
              <div style={{ background: '#f0faf3', border: '1px solid #cfe3d6', borderRadius: 10, padding: '16px' }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#1a5c2e', margin: '0 0 10px', paddingBottom: 8, borderBottom: '1px solid #cfe3d6' }}>📧 Email 1 — Bienvenida</p>
                <div style={{ fontSize: 12, color: '#444', lineHeight: 1.8 }}>
                  <div><strong>Para:</strong> Correo personal del empleado</div>
                  <div><strong>CC:</strong> jcascales@valorizasm.com<br />&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;dmaldonadoENTELGY@valorizasm.com<br />&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;smorin@valorizasm.com</div>
                  <div style={{ marginTop: 8 }}><strong>Contenido:</strong></div>
                  <ul style={{ margin: '4px 0', paddingLeft: 16, lineHeight: 1.9 }}>
                    <li>Datos de acceso del empleado</li>
                    <li>Aviso: contraseña en 2.º correo</li>
                    <li>Formulario Recibí material IT (link + QR)</li>
                    <li>Guía de buenas prácticas</li>
                    <li>Portal TREBOL + Intranet MyHub</li>
                  </ul>
                </div>
              </div>
              <div style={{ background: '#fff8e1', border: '1px solid #fde68a', borderRadius: 10, padding: '16px' }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#92400e', margin: '0 0 10px', paddingBottom: 8, borderBottom: '1px solid #fde68a' }}>🔐 Email 2 — Contraseña</p>
                <div style={{ fontSize: 12, color: '#444', lineHeight: 1.8 }}>
                  <div><strong>Para:</strong> Correo personal del empleado</div>
                  <div><strong>CC:</strong> <em style={{ color: '#888' }}>Ninguno (privacidad)</em></div>
                  <div style={{ marginTop: 8 }}><strong>Contenido:</strong></div>
                  <ul style={{ margin: '4px 0', paddingLeft: 16, lineHeight: 1.9 }}>
                    <li>Contraseña temporal destacada</li>
                    <li>Pasos de primer acceso a Office 365</li>
                    <li>Aviso de seguridad</li>
                    <li>Instrucción de cambio de contraseña</li>
                  </ul>
                  <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(249,168,37,0.1)', borderRadius: 6, fontSize: 11, color: '#92400e' }}>
                    ⚠️ Este correo no lleva CC para proteger la contraseña
                  </div>
                </div>
              </div>
            </div>

            <SubSection title="Ejemplo real — Email 1: Bienvenida">
              <EmailWelcomePreview />
            </SubSection>

            <div style={{ height: 32 }} />

            <SubSection title="Ejemplo real — Email 2: Contraseña temporal">
              <EmailPasswordPreview />
            </SubSection>
          </Section>

          {/* SECTION 5 */}
          <Section num="5" title="Pantalla de confirmación">
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 16 }}>
              Tras procesar el alta correctamente, el sistema muestra una pantalla de confirmación con el resumen del registro y el estado de los correos enviados.
            </p>
            <InfoBox icon="✅" color="#1a5c2e" bg="#f0faf3" border="#cfe3d6" title="Qué muestra la confirmación">
              Estado de los dos emails enviados (Enviado / En cola / Error), resumen de todos los datos del alta registrada y botón para iniciar una nueva alta sin necesidad de recargar la página.
            </InfoBox>
            <div style={{ background: '#f9fdf9', border: '1px solid #cfe3d6', borderRadius: 12, padding: '24px', textAlign: 'center' }}>
              <div style={{ display: 'inline-block', background: 'linear-gradient(135deg, #1a5c2e, #3aaa62)', borderRadius: 10, padding: '8px 18px', marginBottom: 16 }}>
                <img src="/valoriza-logo.svg" alt="Valoriza" style={{ height: 24, display: 'block' }} />
              </div>
              <div style={{ fontSize: 40, margin: '8px 0' }}>🎉</div>
              <h3 style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a5c2e', margin: '0 0 8px' }}>Alta procesada correctamente</h3>
              <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>Correos enviados a jcascales@valorizasm.com</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ background: '#f0faf3', border: '1px solid #cfe3d6', borderRadius: 20, padding: '6px 16px', fontSize: 13, color: '#1a5c2e' }}>✅ Bienvenida: <strong>sent</strong></div>
                <div style={{ background: '#f0faf3', border: '1px solid #cfe3d6', borderRadius: 20, padding: '6px 16px', fontSize: 13, color: '#1a5c2e' }}>✅ Contraseña: <strong>sent</strong></div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, textAlign: 'left' }}>
                {[['Nombre','Juan Martínez López'],['Correo corp.','j.martinez@valorizasm.com'],['Departamento','Operaciones'],['Puesto','Técnico Medioambiental'],['Incorporación','01/07/2026'],['Teléfono','+34 612 345 678']].map(([l,v])=>(
                  <div key={l} style={{ background: '#f9fdf9', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>{l}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* SECTION 6 */}
          <Section num="6" title="Panel de control y KPIs">
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 16 }}>
              El Panel de Control ofrece visibilidad completa sobre todas las altas realizadas, con métricas en tiempo real y herramientas de análisis.
            </p>
            <Screenshot src={s_dashboard} caption="Panel de Control — KPIs, gráficos de tendencia y distribución por departamento." />
            <SubSection title="KPIs disponibles">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 8 }}>
                {[
                  { icon: '🏆', title: 'Total Altas', desc: 'Número total de altas registradas desde el inicio' },
                  { icon: '📅', title: 'Este mes', desc: 'Altas registradas en el mes en curso' },
                  { icon: '🗓', title: 'Esta semana', desc: 'Altas de la semana actual (lunes a domingo)' },
                  { icon: '☀️', title: 'Hoy', desc: 'Altas procesadas el día de hoy' },
                ].map(({ icon, title, desc }) => (
                  <div key={title} style={{ background: '#f9fdf9', border: '1px solid #cfe3d6', borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
                    <p style={{ fontWeight: 700, fontSize: 13, color: '#1a5c2e', margin: '0 0 4px' }}>{title}</p>
                    <p style={{ fontSize: 11, color: '#888', margin: 0, lineHeight: 1.5 }}>{desc}</p>
                  </div>
                ))}
              </div>
            </SubSection>
            <SubSection title="Gráficos y métricas">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                {[
                  { icon: '📈', title: 'Tendencia diaria (14 días)', desc: 'Gráfico de área que muestra la evolución de las altas en los últimos 14 días.' },
                  { icon: '📊', title: 'Distribución por departamento', desc: 'Barras horizontales con el número de altas por cada área de la empresa.' },
                  { icon: '📧', title: 'Estado de emails', desc: 'Contadores de emails enviados correctamente, en cola y con error.' },
                  { icon: '📋', title: 'Registro de altas', desc: 'Tabla paginada con todos los registros, búsqueda por nombre/email y filtro por departamento.' },
                ].map(({ icon, title, desc }) => (
                  <div key={title} style={{ display: 'flex', gap: 10, padding: '12px', background: '#fafafa', borderRadius: 8 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 13, color: '#1a5c2e', margin: '0 0 3px' }}>{title}</p>
                      <p style={{ fontSize: 12, color: '#666', margin: 0, lineHeight: 1.5 }}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </SubSection>
          </Section>

          {/* SECTION 7 */}
          <Section num="7" title="Funcionalidades adicionales">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { icon: '🔄', title: 'Reenvío de correos', desc: 'Desde la tabla del dashboard, el técnico puede reenviar ambos emails (bienvenida + contraseña) a cualquier empleado ya registrado con un solo clic. También disponible desde el modal de detalle.' },
                { icon: '⬇️', title: 'Exportación CSV', desc: 'Botón "Exportar CSV" en el Panel de Control descarga un fichero con todos los registros: nombre, correos, departamento, puesto, fechas y estado de emails.' },
                { icon: '🔍', title: 'Búsqueda y filtros', desc: 'La tabla de registros permite buscar por nombre o correo y filtrar por departamento. Los resultados se actualizan en tiempo real.' },
                { icon: '🪟', title: 'Modal de detalle', desc: 'Al hacer clic en cualquier fila de la tabla, se abre un modal con todos los datos del empleado y opciones de acción.' },
                { icon: '📱', title: 'Diseño responsive', desc: 'La aplicación es completamente responsive: en móvil el sidebar se convierte en un panel deslizante y el formulario se adapta a una columna.' },
                { icon: '🚪', title: 'Gestión de sesión', desc: 'El botón "Cerrar sesión" en el sidebar limpia la sesión y devuelve a la pantalla de login. La sesión persiste mientras el navegador esté abierto.' },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px', display: 'flex', gap: 12 }}>
                  <div style={{ width: 36, height: 36, background: 'rgba(26,92,46,0.07)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{icon}</div>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: 13, color: '#1a5c2e', margin: '0 0 4px' }}>{title}</p>
                    <p style={{ fontSize: 12, color: '#666', margin: 0, lineHeight: 1.6 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24 }}>
              <SubSection title="Destinatarios de email configurados">
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', background: '#f9fdf9', padding: '8px 16px', fontSize: 11, fontWeight: 700, color: '#1a5c2e', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <span>Rol</span><span>Email</span><span>Recibe</span>
                  </div>
                  {[
                    ['Empleado (nuevo)','correo personal del empleado','Email 1 + Email 2'],
                    ['CAU Coordinador','jcascales@valorizasm.com','CC en Email 1'],
                    ['CAU IT','dmaldonadoENTELGY@valorizasm.com','CC en Email 1'],
                    ['RRHH','smorin@valorizasm.com','CC en Email 1'],
                    ['Remitente','innovation@valorizasm.com','— (origen del envío)'],
                  ].map(([rol, email, recibe], i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', padding: '9px 16px', fontSize: 13, borderTop: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <span style={{ fontWeight: rol === 'Empleado (nuevo)' ? 700 : 400, color: '#374151' }}>{rol}</span>
                      <span style={{ color: '#1a5c2e', fontFamily: 'monospace', fontSize: 12 }}>{email}</span>
                      <span style={{ color: '#666', fontSize: 12 }}>{recibe}</span>
                    </div>
                  ))}
                </div>
              </SubSection>
            </div>
          </Section>

          {/* SECTION 8 - POLÍTICAS DE SEGURIDAD */}
          <Section num="8" title="Políticas de seguridad de Valoriza">
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 20 }}>
              Como parte del proceso de onboarding, todos los nuevos empleados deben conocer y cumplir con las políticas de seguridad de Valoriza. Los documentos están disponibles en el email de bienvenida y en el sidebar de la aplicación.
            </p>

            <SubSection title="📋 Puesto de Trabajo: Medidas de Protección">
              <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 12 }}>
                  <strong>Contenido:</strong> Guía esencial sobre medidas de protección de información en el puesto de trabajo, incluyendo:
                </p>
                <ul style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 14, paddingLeft: 24 }}>
                  <li>Mesas limpias y documentación sensible</li>
                  <li>Uso adecuado de Internet y sistemas corporativos</li>
                  <li>Antivirus, Firewall y Software legítimo</li>
                  <li>Bloqueo de sesión y uso seguro de dispositivos</li>
                  <li>Procedimientos para reportar incidentes de seguridad</li>
                </ul>
                <a 
                  href="https://customer-assets.emergentagent.com/job_new-hire-welcome/artifacts/3komy3o1_PUESTO_DE_TRABAJO_v02.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', background: '#2d8a4e', color: '#fff', padding: '10px 20px', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}
                >
                  📥 Descargar PDF
                </a>
              </div>
            </SubSection>

            <SubSection title="📘 Guía de Buenas Prácticas para Activos de Información">
              <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 12 }}>
                  <strong>Contenido:</strong> Normativa completa sobre el uso seguro de los activos de información de Valoriza, que incluye:
                </p>
                <ul style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 14, paddingLeft: 24 }}>
                  <li>Definición y clasificación de información (Pública, Interna, Confidencial, Crítica)</li>
                  <li>Política de contraseñas y acceso a sistemas</li>
                  <li>Uso de correo electrónico, internet y dispositivos móviles</li>
                  <li>Manejo y destrucción de soportes físicos y digitales</li>
                  <li>Teletrabajo y derechos digitales de los empleados</li>
                </ul>
                <a 
                  href="https://customer-assets.emergentagent.com/job_new-hire-welcome/artifacts/82ryg9wg_pt4900_02_GuiaBuenasPracticas_V3.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', background: '#2d8a4e', color: '#fff', padding: '10px 20px', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}
                >
                  📥 Descargar PDF
                </a>
              </div>
            </SubSection>

            <InfoBox icon="⚠️" color="#d97706" bg="#fef3c7" border="#fbbf24" title="Importante">
              Estas políticas son de <strong>cumplimiento obligatorio</strong> para todos los empleados y terceros que manejen información de Valoriza. Se recomienda su lectura completa en los primeros días de incorporación.
            </InfoBox>
          </Section>

          {/* SECTION 9 */}
          <Section num="9" title="Checklist de validación MVP">
            <InfoBox icon="📋" color="#1a5c2e" bg="#f0faf3" border="#cfe3d6" title="Instrucciones para el validador">
              A continuación se listan todas las funcionalidades a validar. Para cada punto, verificar que el comportamiento es el esperado y marcar como aprobado.
            </InfoBox>

            <SubSection title="🔐 Acceso y autenticación">
              <Check>La URL carga correctamente la pantalla de login con branding Valoriza</Check>
              <Check>Con credenciales incorrectas, aparece mensaje de error y animación shake</Check>
              <Check>Con credenciales correctas (altausuarios@valorizasm.com / Val0rizaAlta26!), se accede al sistema</Check>
              <Check>El botón "Cerrar sesión" en el sidebar devuelve a la pantalla de login</Check>
              <Check>Al recargar la página, la sesión se mantiene activa</Check>
            </SubSection>

            <SubSection title="📋 Formulario de Alta">
              <Check>El formulario muestra los 8 campos organizados en 2 columnas</Check>
              <Check>Si se pulsa "Procesar Alta" sin rellenar campos, aparecen mensajes de error en rojo</Check>
              <Check>El dropdown de Departamento muestra 14 opciones correctamente</Check>
              <Check>El toggle "Sin teléfono" desactiva el campo y lo marca como no obligatorio</Check>
              <Check>Todos los campos se limpian al pulsar "Limpiar"</Check>
              <Check>El formulario envía correctamente con todos los campos rellenos</Check>
            </SubSection>

            <SubSection title="📧 Envío de emails">
              <Check>El empleado recibe el <strong>Email 1 (Bienvenida)</strong> en su correo personal con diseño Valoriza</Check>
              <Check>El Email 1 incluye: datos del empleado, link Survey123, TREBOL, Intranet, guía de buenas prácticas</Check>
              <Check>El Email 1 llega en copia a: jcascales@valorizasm.com, dmaldonadoENTELGY@valorizasm.com, smorin@valorizasm.com</Check>
              <Check>El empleado recibe el <strong>Email 2 (Contraseña)</strong> en su correo personal con la contraseña destacada</Check>
              <Check>El Email 2 <strong>NO</strong> lleva destinatarios en copia (privacidad de contraseña)</Check>
              <Check>El remitente de ambos emails es innovation@valorizasm.com</Check>
            </SubSection>

            <SubSection title="✅ Confirmación y registro">
              <Check>Tras el envío, aparece la pantalla de confirmación con el estado de ambos emails (sent/queued)</Check>
              <Check>El resumen de la confirmación muestra todos los datos del alta</Check>
              <Check>El botón "Nueva Alta" reinicia el formulario correctamente</Check>
              <Check>El alta queda registrada en la base de datos y aparece en el dashboard</Check>
            </SubSection>

            <SubSection title="📊 Panel de Control">
              <Check>Los 4 KPIs (Total, Este mes, Esta semana, Hoy) muestran valores correctos</Check>
              <Check>El gráfico de tendencia diaria (14 días) se renderiza correctamente</Check>
              <Check>El gráfico de distribución por departamento refleja los datos reales</Check>
              <Check>Los contadores de estado de email (Enviados / En cola / Con error) son correctos</Check>
              <Check>La tabla muestra todos los registros con nombre, departamento, puesto y estado de emails</Check>
              <Check>El buscador filtra correctamente por nombre y correo</Check>
              <Check>El filtro por departamento funciona correctamente</Check>
              <Check>El botón "Reenviar" en cada fila reenvía los correos correctamente</Check>
              <Check>El botón "Exportar CSV" descarga un fichero con todos los registros</Check>
              <Check>Al hacer clic en una fila, se abre el modal de detalle con toda la información</Check>
            </SubSection>

            <SubSection title="🎨 UX y Diseño">
              <Check>El branding Valoriza (colores verdes, logo oficial) es consistente en todas las pantallas</Check>
              <Check>El texto "Powered by Valoriza's Innovation Team" aparece en footer de todas las pantallas</Check>
              <Check>El copyright muestra © 2026 en todas las pantallas y emails</Check>
              <Check>No aparece el badge "Made with Emergent" en ninguna pantalla</Check>
              <Check>El sidebar muestra el logo Valoriza oficial y el usuario activo</Check>
              <Check>Los emails enviados tienen diseño WOW con branding Valoriza</Check>
            </SubSection>

            <div style={{ marginTop: 28, background: 'linear-gradient(135deg, #f0faf3, #fff)', border: '2px solid #3aaa62', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🚀</div>
              <h3 style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a5c2e', margin: '0 0 8px' }}>¿Todo validado? ¡Listo para producción!</h3>
              <p style={{ color: '#666', fontSize: 13, margin: '0 0 16px' }}>Si todos los puntos anteriores han sido verificados, el sistema está preparado para su despliegue en producción.</p>
              <p style={{ color: '#888', fontSize: 12, margin: 0 }}>Para soporte técnico o incidencias: <strong style={{ color: '#1a5c2e' }}>innovation@valorizasm.com</strong></p>
            </div>
          </Section>

          {/* Footer */}
          <div style={{ textAlign: 'center', paddingTop: 32, borderTop: '1px solid #e5e7eb', marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <div style={{ background: 'linear-gradient(135deg, #1a5c2e, #2d8a4e)', borderRadius: 8, padding: '8px 18px', display: 'inline-block' }}>
                <img src="/valoriza-logo.svg" alt="Valoriza" style={{ height: 22, display: 'block' }} />
              </div>
            </div>
            <p style={{ color: '#bbb', fontSize: 12, margin: '0 0 4px' }}>© 2026 Valoriza Servicios Medioambientales · Todos los derechos reservados</p>
            <p style={{ color: '#ccc', fontSize: 11, margin: 0, fontStyle: 'italic' }}>Powered by Valoriza's Innovation Team · Documento de validación MVP v1.0</p>
          </div>

        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { margin: 15mm 12mm; size: A4; }
        }
      `}</style>
    </div>
  );
}
